<template>
  <IonPage
    class="main-layout"
    :class="{
      'drawer-open': uiStore.isDrawerOpen && layoutContext === LAYOUT_CONTEXT.DESKTOP_WEB,
      'drawer-collapsed': !uiStore.isDrawerOpen && layoutContext === LAYOUT_CONTEXT.DESKTOP_WEB,
      'is-native-mobile': layoutContext === LAYOUT_CONTEXT.NATIVE_MOBILE,
      'is-mobile-web': layoutContext === LAYOUT_CONTEXT.MOBILE_WEB,
      'is-desktop-web': layoutContext === LAYOUT_CONTEXT.DESKTOP_WEB,
      'is-android': isNativeAndroid,
      'is-ios': isNativeIOS,
      'is-student': userRole === USER_TYPE.STUDENT,
      'is-collaborator': userRole === USER_TYPE.COLLABORATEUR,
    }"
  >
    <IonContent
      id="main-content"
      :class="{
        'mobile-content': layoutContext !== LAYOUT_CONTEXT.DESKTOP_WEB,
        'is-android': isNativeAndroid,
        'is-ios': isNativeIOS,
      }"
    >
      <div
        class="content-wrapper"
        :class="{
          'theme-not-modern': themeIsntModern,
          'theme-not-modern-and-student-home': themeIsntModern && routeIsStudentHome,
          'is-mobile-web': layoutContext === LAYOUT_CONTEXT.MOBILE_WEB,
        }"
        ref="contentWrapper"
      >
        <slot></slot>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { computed, ref, watch, nextTick, onMounted } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { useDeviceDetection } from '@/common/composables/useDeviceDetection';
import { useUser } from '@/common/composables/useUser';
import { USER_TYPE } from '@/common/constants';
import { LAYOUT_CONTEXT } from '@/layouts/constants';
import { useUIStore } from '@/common/stores/uiStore';
import { useAnimationStore } from '@/common/stores/animationStore';
import useTheming from '@/common/composables/useTheming';

const { layoutContext, isNativeAndroid, isNativeIOS } = useDeviceDetection();
const { userRole } = useUser();
const uiStore = useUIStore();
const animationStore = useAnimationStore();
const route = useRoute();
const { themeMode } = useTheming();
const router = useRouter();

const themeIsntModern = computed(() => themeMode.value !== 'modern');

const routeIsStudentHome = computed(() => {
  return router.currentRoute.value.path === '/student-home';
});

console.log('LayoutContext:', routeIsStudentHome.value);
// Template ref for content wrapper
const contentWrapper = ref<HTMLElement | null>(null);

// Animate content when route changes
watch(
  () => route.name,
  async (newRouteName, oldRouteName) => {
    if (newRouteName && newRouteName !== oldRouteName && contentWrapper.value) {
      await nextTick();

      // Get animation type based on route
      const animationType = animationStore.getAnimationForRoute(newRouteName as string);

      // Play animation
      animationStore.playContentAnimation(contentWrapper.value, animationType);

      // Re-style shadow DOM after route change
      await waitForShadowDOM();
    }
  }
);

// Function to style the ion-content shadow DOM
const styleShadowContent = () => {
  const ionContent = document.querySelector('ion-content');
  const shadowRoot = ionContent?.shadowRoot;

  if (!shadowRoot) return false;

  const backgroundContent = shadowRoot.querySelector('#background-content') as HTMLElement;

  if (backgroundContent) {
    backgroundContent.style.backgroundColor = 'transparent';
    backgroundContent.style.borderRadius = '8px 0 0 0';
    backgroundContent.style.padding = '0';
    // backgroundContent.style.zIndex = '1';
    return true;
  }

  return false;
};

// Function to wait for shadow DOM to be ready
const waitForShadowDOM = async (maxAttempts = 10, delay = 50) => {
  for (let i = 0; i < maxAttempts; i++) {
    if (styleShadowContent()) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
  return false;
};

// Initial animation on mount
onMounted(async () => {
  if (contentWrapper.value && route.name) {
    await nextTick();
    const animationType = animationStore.getAnimationForRoute(route.name as string);
    animationStore.playContentAnimation(contentWrapper.value, animationType);

    // Wait for shadow DOM to be ready
    await waitForShadowDOM();
  }
});
</script>

<style scoped>
.main-layout {
  height: 100vh;
  transition: all 0.3s ease;
}

/* ============================================
   DESKTOP WEB - Navigation avec drawer
   ============================================ */
.main-layout.is-desktop-web {
  padding-left: 70px;
  margin-top: var(--student-topbar-height);
}

.main-layout.is-desktop-web.drawer-open {
  margin-left: 250px;
  width: calc(100% - 250px);
}

.main-layout.is-desktop-web.drawer-collapsed {
  margin-left: 60px;
  width: calc(100% - 60px);
}

/* ============================================
   MOBILE WEB - Navigateur mobile
   ============================================ */
.main-layout.is-mobile-web {
  padding-left: 0;
  margin-top: 0;
}

/* ============================================
   NATIVE MOBILE - App Capacitor iOS/Android
   ============================================ */
.main-layout.is-native-mobile {
  padding-left: 0;
}

.main-layout.is-native-mobile.is-android {
  margin-top: 40px;
}

.main-layout.is-native-mobile.is-ios {
  margin-top: 10px;
}

/* ============================================
   CONTENT WRAPPER
   ============================================ */
.content-wrapper {
  height: calc(100% - 56px);
  overflow: hidden;
  border-radius: 3vmin 0 0 0;
}

.content-wrapper.theme-not-modern {
  border: 1px solid var(--ion-input-border-color);
  border-right: 1px solid transparent;
}

.content-wrapper.theme-not-modern-and-student-home {
  border: 1px solid transparent;
}

.content-wrapper.is-mobile-web {
  height: 100%; /* Prend toute la hauteur disponible du parent (déjà réduit) */
  border-radius: 0;
}

.main-content {
  background-color: red;
}

.with-header {
  padding-top: 56px;
}

.header-toolbar {
  --background: var(--ion-background-color);
  box-shadow: var(--ion-box-shadow);
}
</style>
